/**
 * AIScheduler Bundle — B, C, D, F, G, H, I, J (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * Consolidated module that exposes all eight in-app AI scheduler features
 * so the scheduler parent can mount them as tabs or inline panels without
 * juggling 8 imports.
 *
 * Features:
 *   B — RiskBreakdownV2           : explainable risk + auto-picked intervention
 *   C — DynamicRecallEngine       : care-gap driven recall queue
 *   D — TwoWaySmartReminders      : conversational reschedule threads
 *   F — RoomingOrchestrator       : AI-proposed swaps to recover clinic time
 *   G — DemandForecast            : 90-day utilization + template tune
 *   H — PatientPreferencesMemory  : per-patient comms prefs carry across flows
 *   I — MultiLocationRouting      : route flexible demand to best location
 *   J — SchedulerCommandCenter    : KPI cockpit with Echo commentary
 *
 * Each section is self-contained, purely demo (no API calls), and uses a
 * single cohesive grayscale visual language with the Echo sparkle accent.
 */
import { useState } from 'react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import { toast } from 'sonner';
import {
  AlertTriangle, TrendingUp, TrendingDown, ArrowRight, Sparkles, CheckCircle2,
  MessageSquare, Phone, Mail, Bell, MapPin, Activity, Zap, Clock,
  ChevronRight, Heart, CalendarClock, Send, Users, RefreshCw, BarChart3,
} from 'lucide-react';
import { useAppMode } from '../../../../contexts/AppModeContext';

/* ─────────── B · Risk Breakdown v2 ─────────── */
const DEMO_RISK_V2 = {
  patient: 'David Wilson',
  appt: '2:00 PM today · New Patient · Dr. Wilson',
  overall: 68,
  features: [
    { key: 'prior-no-shows', name: 'Prior no-shows (last 12mo)', value: '3', weight: 24, direction: 'up' },
    { key: 'reminder-opened', name: 'Reminder opened?', value: 'No', weight: 18, direction: 'up' },
    { key: 'rain-forecast', name: 'Rain forecast at appt time', value: '72% chance', weight: 11, direction: 'up' },
    { key: 'distance', name: 'Distance to clinic', value: '14 miles', weight: 9, direction: 'up' },
    { key: 'balance', name: 'Balance owed', value: '$85', weight: 6, direction: 'up' },
    { key: 'same-day-confirm', name: 'Same-day phone confirm', value: 'Not yet', weight: 0, direction: 'neutral' },
  ],
  recommended: {
    action: 'Live phone nudge + $10 copay waiver offer',
    reason: 'Best lift vs. simple SMS given the 3-no-show history; phone + money-on-the-table has 3× conversion in this risk band.',
    alt: [
      { label: 'SMS follow-up only', lift: '+7%' },
      { label: 'Voice AI reminder call', lift: '+19%' },
      { label: 'Pre-emptive overbook', lift: '+34%' },
    ],
  },
};

export const RiskBreakdownV2 = () => {
  const r = DEMO_RISK_V2;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4" data-testid="risk-v2">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Explainable Risk · v2</p>
          <p className="text-[13px] font-semibold text-slate-800 mt-0.5">{r.patient}</p>
          <p className="text-[11px] text-slate-500">{r.appt}</p>
        </div>
        <div className="text-right">
          <p className="text-[26px] font-bold text-red-600 leading-none">{r.overall}%</p>
          <p className="text-[9px] text-slate-400 uppercase">no-show risk</p>
        </div>
      </div>
      <div className="space-y-1.5">
        {r.features.map((f) => (
          <div key={f.key} className="flex items-center gap-2" data-testid={`risk-feature-${f.key}`}>
            <div className="w-36 text-[11px] text-slate-600">{f.name}</div>
            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full', f.direction === 'up' ? 'bg-red-500' : f.direction === 'down' ? 'bg-emerald-500' : 'bg-slate-300')}
                style={{ width: `${Math.min(100, f.weight * 4)}%` }}
              />
            </div>
            <div className="w-24 text-right text-[10px] text-slate-500">{f.value}</div>
            <div className="w-10 text-right text-[10px] font-semibold text-slate-700">+{f.weight}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-slate-900 text-white p-3 space-y-2" data-testid="risk-v2-recommendation">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" />
          <p className="text-[11px] font-semibold">Echo recommends</p>
        </div>
        <p className="text-[12px] font-semibold">{r.recommended.action}</p>
        <p className="text-[10px] text-slate-300">{r.recommended.reason}</p>
        <div className="flex flex-wrap gap-1 pt-1">
          {r.recommended.alt.map((a) => (
            <span key={a.label} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/10">{a.label} · {a.lift}</span>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <Button size="sm" className="h-7 text-[10px] bg-white text-slate-900 hover:bg-slate-100 gap-1" onClick={() => toast.success(isDemo ? 'Calling David Wilson — waiver offer queued' : 'Action queued')}>
            <Phone className="h-3 w-3" /> Apply recommended
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[10px] text-white hover:bg-white/10">See alternatives</Button>
        </div>
      </div>
    </div>
  );
};

/* ─────────── C · Dynamic Recall Engine ─────────── */
const DEMO_RECALL = [
  { id: 'r1', patient: 'Maria Garcia', gap: 'Annual Wellness overdue by 42 days', value: '$245', channel: 'SMS + email', status: 'drafted' },
  { id: 'r2', patient: 'James Taylor', gap: 'HbA1c recheck due (Diabetes)', value: '$180', channel: 'SMS', status: 'sent' },
  { id: 'r3', patient: 'Robert Johnson', gap: 'Mammogram due in 14 days', value: '$310', channel: 'Email', status: 'booked' },
  { id: 'r4', patient: 'Lisa Brown', gap: 'BP follow-up overdue by 21 days', value: '$95', channel: 'Call', status: 'drafted' },
  { id: 'r5', patient: 'Emily Davis', gap: 'Depression screen overdue', value: '$120', channel: 'SMS', status: 'sent' },
];

export const DynamicRecallEngine = () => {
  const booked = DEMO_RECALL.filter((r) => r.status === 'booked').length;
  const totalValue = DEMO_RECALL.reduce((s, r) => s + parseInt(r.value.replace(/[^0-9]/g, '')), 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4" data-testid="recall-engine">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Care Gap Recall · Echo-driven</p>
          <p className="text-[13px] font-semibold text-slate-800 mt-0.5">Dynamic Recall Engine</p>
          <p className="text-[11px] text-slate-500">Drafted outreach for patients with overdue care · direct-book links embedded</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1">
            <RefreshCw className="h-3 w-3" /> Re-scan EHR
          </Button>
          <Button size="sm" className="h-8 text-[11px] bg-[#111] hover:bg-[#222] gap-1" onClick={() => toast.success('5 outreaches scheduled · Echo tracking replies')} data-testid="recall-send-all-btn">
            <Send className="h-3 w-3" /> Send all drafts
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat testId="recall-kpi-care-gaps" label="Care gaps detected" value={DEMO_RECALL.length} />
        <Stat testId="recall-kpi-booked" label="Already booked" value={booked} trend="up" />
        <Stat testId="recall-kpi-revenue" label="Recoverable revenue" value={`$${totalValue.toLocaleString()}`} accent />
      </div>
      <div className="space-y-1">
        {DEMO_RECALL.map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 hover:border-slate-300" data-testid={`recall-row-${r.id}`}>
            <Heart className="h-3 w-3 text-slate-400" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-slate-800">{r.patient}</p>
              <p className="text-[10px] text-slate-500">{r.gap}</p>
            </div>
            <span className="text-[10px] text-slate-500">{r.channel}</span>
            <span className="text-[11px] font-semibold text-emerald-600">{r.value}</span>
            <StatusPill status={r.status} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────── D · Two-Way Smart Reminders ─────────── */
const DEMO_THREAD = [
  { who: 'echo', text: "Hi Emily! This is Echo on behalf of Riverside Family Medicine. Reminder: Follow-up tomorrow 10:30 AM with Dr. Chen. Reply C to confirm, 1 for 9 AM, 2 for 2:30 PM, 3 to reschedule.", ts: '9:02 AM' },
  { who: 'patient', text: '2', ts: '9:14 AM' },
  { who: 'echo', text: "Got it — moving you to 2:30 PM with Dr. Chen. You'll get a confirmation text in a moment. Anything else?", ts: '9:14 AM' },
  { who: 'patient', text: 'Any copay?', ts: '9:15 AM' },
  { who: 'echo', text: "Your plan shows $20 specialist copay for this visit. You can pay at the front desk or in advance via the link below.", ts: '9:15 AM' },
];

export const TwoWaySmartReminders = () => {
  const { isDemo } = useAppMode();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="two-way-reminders">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Two-Way Reminders</p>
          <p className="text-[13px] font-semibold text-slate-800 mt-0.5">{isDemo ? 'Emily Davis · SMS thread' : 'Patient · SMS thread'}</p>
          <p className="text-[11px] text-slate-500">Echo handles confirm, reschedule, copay Q&amp;A · escalates to staff only when needed</p>
        </div>
        <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">Auto-resolved</Badge>
      </div>
      <div className="rounded-xl bg-slate-50 p-3 space-y-2 max-h-[320px] overflow-y-auto">
        {DEMO_THREAD.map((m, i) => (
          <div key={i} className={cn('flex', m.who === 'echo' ? 'justify-start' : 'justify-end')}>
            <div className={cn('max-w-[80%] rounded-2xl px-3 py-2',
              m.who === 'echo' ? 'bg-white border border-slate-200' : 'bg-slate-900 text-white'
            )}>
              <p className={cn('text-[11px]', m.who === 'echo' ? 'text-slate-700' : 'text-white')}>{m.text}</p>
              <p className={cn('text-[9px] mt-1', m.who === 'echo' ? 'text-slate-400' : 'text-slate-300')}>{m.ts}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4 gap-2 pt-1">
        <MiniStat icon={Bell} label="Sent today" value="128" />
        <MiniStat icon={CheckCircle2} label="Confirmed" value="92" />
        <MiniStat icon={CalendarClock} label="Rescheduled" value="14" />
        <MiniStat icon={Phone} label="Escalated" value="3" />
      </div>
    </div>
  );
};

/* ─────────── F · Rooming Orchestrator ─────────── */
const DEMO_SWAPS = [
  {
    id: 's1',
    title: 'Dr. Chen is running 18 min behind.',
    proposal: 'Swap 2:00 PM (Robert Johnson · Sick Visit 20m) with 2:30 PM (Sarah Williams · Follow-up 20m). Recovers 18 min and keeps both patients ≤10 min late.',
    impact: { patientsAffected: 2, timeRecovered: '18 min', revenue: '$0' },
  },
  {
    id: 's2',
    title: 'Empty 3:30 PM slot (Dr. Wilson).',
    proposal: 'Pull Priya Patel from waitlist (Sick Visit · 73% arrival) OR move Emily Davis (4 PM Follow-up) forward to 3:30 — frees 4 PM for potential walk-in.',
    impact: { patientsAffected: 1, timeRecovered: '30 min', revenue: '+$125' },
  },
];

export const RoomingOrchestrator = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="rooming-orchestrator">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Rooming Orchestrator</p>
        <p className="text-[13px] font-semibold text-slate-800 mt-0.5">2 suggested swaps</p>
        <p className="text-[11px] text-slate-500">Echo re-plans the next 2 hours when a provider falls behind or a slot opens up.</p>
      </div>
      <Badge className="text-[9px] bg-slate-900 text-white border-0 gap-1">
        <Sparkles className="h-2.5 w-2.5" /> Live re-plan
      </Badge>
    </div>
    <div className="space-y-2">
      {DEMO_SWAPS.map((s) => (
        <div key={s.id} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`orchestrator-row-${s.id}`}>
          <p className="text-[12px] font-semibold text-slate-800">{s.title}</p>
          <p className="text-[11px] text-slate-600">{s.proposal}</p>
          <div className="flex items-center gap-3 text-[10px] text-slate-500">
            <span className="inline-flex items-center gap-1"><Users className="h-2.5 w-2.5" /> {s.impact.patientsAffected} patients</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> {s.impact.timeRecovered} recovered</span>
            <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">{s.impact.revenue}</span>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="h-7 text-[10px] bg-[#111] hover:bg-[#222] gap-1" onClick={() => toast.success('Swap applied — patients notified')} data-testid={`orchestrator-apply-${s.id}`}>
              <CheckCircle2 className="h-3 w-3" /> One-click apply
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px]">Preview</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400">Dismiss</Button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ─────────── G · Demand Forecast + Template Auto-Tune ─────────── */
const DEMO_DEMAND = [
  { provider: 'Dr. Chen', utilization: 94, trend: 'up', suggestion: 'Add 2 morning slots on Thursdays', revenueLift: '+$1,240/mo' },
  { provider: 'Dr. Wilson', utilization: 78, trend: 'down', suggestion: 'Rebalance Tue afternoon blocks to 45-min visits', revenueLift: '+$680/mo' },
  { provider: 'Dr. Roberts', utilization: 61, trend: 'down', suggestion: 'Consolidate to 3-day schedule; no new-patient demand mid-week', revenueLift: '-$0 (coverage only)' },
];

export const DemandForecast = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="demand-forecast">
    <div>
      <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Demand Forecast · 90-day window</p>
      <p className="text-[13px] font-semibold text-slate-800 mt-0.5">Provider template auto-tune</p>
    </div>
    <div className="space-y-1.5">
      {DEMO_DEMAND.map((p) => (
        <div key={p.provider} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200" data-testid={`demand-row-${p.provider.replace(/\W+/g, '-')}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[12px] font-medium text-slate-800">{p.provider}</p>
              {p.trend === 'up' ? <TrendingUp className="h-3 w-3 text-emerald-600" /> : <TrendingDown className="h-3 w-3 text-amber-600" />}
              <span className={cn('text-[10px] font-semibold', p.utilization > 90 ? 'text-red-600' : p.utilization > 75 ? 'text-emerald-600' : 'text-amber-600')}>
                {p.utilization}% utilized
              </span>
            </div>
            <p className="text-[10px] text-slate-500 mt-0.5">{p.suggestion}</p>
          </div>
          <span className="text-[11px] font-semibold text-emerald-600">{p.revenueLift}</span>
          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => toast.success(`Applied: ${p.provider}`)}>Apply</Button>
        </div>
      ))}
    </div>
  </div>
);

/* ─────────── H · Patient Preferences Memory ─────────── */
const DEMO_PREFS = [
  { patient: 'John Smith', prefs: ['SMS preferred · 8 AM', 'Avoid Fridays', 'Male provider OK', 'English'] },
  { patient: 'Maria Garcia', prefs: ['Email + phone', 'Spanish interpreter', 'Early morning', 'Wheelchair access'] },
  { patient: 'James Taylor', prefs: ['Voice calls only', 'Thursday/Friday', 'Dr. Chen preferred', 'Balance on file'] },
];

export const PatientPreferencesMemory = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="patient-prefs-memory">
    <div>
      <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Preferences Memory</p>
      <p className="text-[13px] font-semibold text-slate-800 mt-0.5">Per-patient comms &amp; visit prefs</p>
      <p className="text-[11px] text-slate-500">Carries across reminders, recalls, and scheduling without staff having to re-enter anything.</p>
    </div>
    <div className="space-y-1.5">
      {DEMO_PREFS.map((p) => (
        <div key={p.patient} className="rounded-lg border border-slate-200 px-3 py-2" data-testid={`pref-row-${p.patient.replace(/\W+/g, '-')}`}>
          <p className="text-[12px] font-medium text-slate-800">{p.patient}</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {p.prefs.map((x) => (
              <span key={x} className="text-[9px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700">{x}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ─────────── I · Multi-Location Routing ─────────── */
const DEMO_ROUTING = {
  patientSample: 'Patient Priya P. requested: "any Dermatology next week"',
  ranked: [
    { location: 'Main Office · 100 Medical Pkwy', distance: '4.2 mi', waitDays: 3, inNetwork: true, chosen: true, rationale: 'Shortest wait + in-network + closest' },
    { location: 'Westside Annex · 220 West Park', distance: '6.1 mi', waitDays: 5, inNetwork: true, rationale: 'Backup · same provider pool' },
    { location: 'North Satellite · 18 Oak Ave', distance: '9.8 mi', waitDays: 2, inNetwork: false, rationale: 'Fastest but out-of-network' },
  ],
};

export const MultiLocationRouting = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="multi-location-routing">
    <div>
      <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Multi-Location Routing</p>
      <p className="text-[13px] font-semibold text-slate-800 mt-0.5">Flexible demand → best location</p>
      <p className="text-[11px] text-slate-500 italic mt-0.5">{DEMO_ROUTING.patientSample}</p>
    </div>
    <div className="space-y-1.5">
      {DEMO_ROUTING.ranked.map((r) => (
        <div key={r.location} className={cn('flex items-center gap-3 px-3 py-2 rounded-lg border',
          r.chosen ? 'border-slate-900 bg-slate-50' : 'border-slate-200')} data-testid={`route-row-${r.location.replace(/\W+/g, '-')}`}>
          <MapPin className={cn('h-3 w-3', r.chosen ? 'text-slate-900' : 'text-slate-400')} />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-slate-800">{r.location}</p>
            <p className="text-[10px] text-slate-500">{r.distance} · {r.waitDays}d wait · {r.inNetwork ? 'In-network' : 'Out-of-network'} · {r.rationale}</p>
          </div>
          {r.chosen && <Badge className="text-[9px] bg-slate-900 text-white border-0">Chosen</Badge>}
        </div>
      ))}
    </div>
  </div>
);

/* ─────────── J · Scheduler Command Center ─────────── */
export const SchedulerCommandCenter = () => {
  const { isDemo } = useAppMode();
  return (
  <div className="space-y-4" data-testid="scheduler-command-center">
    <div className="rounded-2xl bg-slate-900 text-white p-4 flex items-start gap-3" data-testid="cmd-echo-commentary">
      <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
      <div>
        <p className="text-[11px] font-semibold text-slate-200">Echo&apos;s read on today</p>
        <p className="text-[13px] font-semibold mt-0.5">{isDemo
          ? 'Overall utilization is tracking +4% vs last Thursday. Dr. Chen is at 94% (hot). Two no-show risks have acknowledged their reminders — likely to attend. 1 open slot at 3:30 has 3 ranked waitlist matches.'
          : 'Echo will narrate today\u2019s schedule once enough live data is available to compare.'}</p>
      </div>
    </div>
    <div className="grid grid-cols-4 gap-3">
      <KPI label="Schedule utilization" value="87%" delta="+4%" dir="up" />
      <KPI label="No-show rate (30d)" value="6.2%" delta="-1.8%" dir="down" positive />
      <KPI label="Reminder CTR" value="72%" delta="+5%" dir="up" positive />
      <KPI label="Revenue / open slot" value="$185" delta="+$22" dir="up" positive />
      <KPI label="Waitlist fill rate" value="68%" delta="+9%" dir="up" positive />
      <KPI label="Avg wait time" value="11 min" delta="-2 min" dir="down" positive />
      <KPI label="AI auto-resolutions" value="142" delta="+18" dir="up" positive />
      <KPI label="Staff escalations" value="7" delta="-3" dir="down" positive />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <DemandForecast />
      <MultiLocationRouting />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <PatientPreferencesMemory />
      <TwoWaySmartReminders />
    </div>
  </div>
  );
};

/* ─────────── Small primitives ─────────── */
const Stat = ({ label, value, trend, accent, testId }) => (
  <div className="rounded-lg bg-slate-50 p-2.5" data-testid={testId}>
    <p className="text-[9px] text-slate-500 uppercase font-semibold">{label}</p>
    <p className={cn('text-[20px] font-bold leading-none mt-1', accent ? 'text-emerald-600' : 'text-slate-800')}>{value}</p>
    {trend === 'up' && <p className="text-[9px] text-emerald-600 inline-flex items-center gap-0.5 mt-0.5"><TrendingUp className="h-2.5 w-2.5" /> trending up</p>}
  </div>
);
const StatusPill = ({ status }) => {
  const map = {
    drafted: { bg: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Drafted' },
    sent: { bg: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Sent' },
    booked: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Booked' },
  };
  const m = map[status] || map.drafted;
  return <span className={cn('text-[9px] border px-1.5 py-0.5 rounded-full', m.bg)}>{m.label}</span>;
};
const MiniStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg bg-slate-50 p-2 text-center">
    <Icon className="h-3 w-3 text-slate-400 mx-auto" />
    <p className="text-[13px] font-bold text-slate-800 mt-0.5">{value}</p>
    <p className="text-[9px] text-slate-500 uppercase">{label}</p>
  </div>
);
const KPI = ({ label, value, delta, dir, positive }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`kpi-${label.toLowerCase().replace(/\W+/g, '-')}`}>
    <p className="text-[9px] text-slate-500 uppercase font-semibold">{label}</p>
    <p className="text-[20px] font-bold text-slate-900 mt-1">{value}</p>
    <p className={cn('text-[10px] mt-1 inline-flex items-center gap-0.5',
      positive ? 'text-emerald-600' : dir === 'up' ? 'text-emerald-600' : 'text-red-600'
    )}>
      {dir === 'up' ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
      {delta} vs last period
    </p>
  </div>
);
